import {
  Component, HostListener, OnInit, computed, inject, signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AssetService } from '../../services/asset.service';

@Component({
  selector: 'app-space-embed-preview',
  standalone: true,
  imports: [LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './space-embed-preview.component.html',
})
export class SpaceEmbedPreviewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private assetSvc = inject(AssetService);
  private sanitizer = inject(DomSanitizer);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly spaceName = signal('Tu espacio');
  readonly embedPath = signal('');
  readonly frameHeight = signal(520);

  /** Same-origin embed URL — bypass required for iframe [src] binding. */
  readonly embedSafeUrl = computed<SafeResourceUrl | null>(() => {
    const path = this.embedPath();
    if (!path) return null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(`${origin}${path}`);
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id')?.trim();
    if (!id) {
      this.error.set('Espacio no encontrado.');
      this.loading.set(false);
      return;
    }
    this.embedPath.set(`/embed/espacio/${id}`);
    void this.#loadSpace(id);
  }

  @HostListener('window:message', ['$event'])
  onEmbedResize(event: MessageEvent): void {
    if (event.data?.type !== 'timeflexrent:resize') return;
    const height = Number(event.data.height);
    if (!Number.isFinite(height) || height <= 0) return;
    this.frameHeight.set(Math.max(420, Math.ceil(height)));
  }

  async #loadSpace(id: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const space = await this.assetSvc.getSpaceById(id);
      if (!space) {
        this.error.set('Espacio no encontrado o no publicado.');
        return;
      }
      this.spaceName.set(space.name?.trim() || 'Tu espacio');
    } catch {
      this.error.set('No se pudo cargar la vista previa.');
    } finally {
      this.loading.set(false);
    }
  }
}
